import 'whatwg-fetch';
import Immutable from 'immutable';

import BaseFetcher from 'src/fetchers/BaseFetcher';
import Reaction from 'src/models/Reaction';
import AttachmentFetcher from 'src/fetchers/AttachmentFetcher';
import Literature from 'src/models/Literature';
import GenericElsFetcher from 'src/fetchers/GenericElsFetcher';
import ResearchPlansFetcher from 'src/fetchers/ResearchPlansFetcher';
import GasPhaseReactionActions from 'src/stores/alt/actions/GasPhaseReactionActions';
import WeightPercentageReactionActions from 'src/stores/alt/actions/WeightPercentageReactionActions';
import { presence, review } from 'src/chemkit';

// TODO: Extract common base functionality into BaseFetcher
export default class ReactionsFetcher {
  static async fetchById(id) {
    try {
      const response = await fetch(`/api/v1/reactions/${id}.json`, {
        credentials: 'same-origin'
      }).then((fetchResponse) => {
        if (!fetchResponse.ok) {
          throw new Error(`Reaction fetch failed: ${fetchResponse.status} ${fetchResponse.statusText}`);
        }
        return fetchResponse.json();
      })
        .then((json) => {
          review.rememberReviewState(json?.reaction?.id, json?.reaction?.chemkit?.review?.state);
          const userLabels = json?.reaction?.tag?.taggable_data?.user_labels || null;
          if (json.hasOwnProperty('reaction')) {
            const reaction = new Reaction(json.reaction);
            const { catalystMoles, vesselSize } = reaction.findReactionVesselSizeCatalystMaterialValues();
            if (vesselSize) {
              GasPhaseReactionActions.setReactionVesselSize(vesselSize);
            }
            if (catalystMoles) {
              GasPhaseReactionActions.setCatalystReferenceMole(catalystMoles);
            }
            const { weightPercentageReference, targetAmount } = reaction.findWeightPercentageReferenceMaterial();
            if (weightPercentageReference) {
              WeightPercentageReactionActions.setWeightPercentageReference(weightPercentageReference);
              WeightPercentageReactionActions.setTargetAmountWeightPercentageReference(targetAmount);
            }
            if (json.literatures && json.literatures.length > 0) {
              const tliteratures = json.literatures.map((literature) => new Literature(literature));
              const lits = tliteratures.reduce((acc, l) => acc.set(l.literal_id, l), new Immutable.Map());
              reaction.literatures = lits;
            }
            if (json.research_plans && json.research_plans.length > 0) {
              reaction.research_plans = json.research_plans;
            }
            reaction.updateMaxAmountOfProducts();
            if (!userLabels) reaction.user_labels = userLabels;
            return reaction;
          }
          const rReaction = new Reaction(json.reaction);
          if (!userLabels) rReaction.setUserLabels(userLabels);
          if (json.error) {
            rReaction.id = `${id}:error:Reaction ${id} is not accessible!`;
          }
          return rReaction;
        });
      const researchPlans = await ResearchPlansFetcher.fetchResearchPlansForElements(id, response.type);
      response.research_plans = researchPlans;
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static fetchByCollectionId(id, queryParams = {}, isSync = false) {
    return BaseFetcher.fetchByCollectionId(id, queryParams, isSync, 'reactions', Reaction);
  }

  static findByShortLabel(shortLabel) {
    return fetch(
      `/api/v1/reactions/findByShortLabel/${shortLabel}.json`,
      {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      }
    ).then((response) => response.json()).catch((errorMessage) => console.log(errorMessage));
  }

  static async create(reaction, method = 'post') {
    const trigger = window.__CHEMKIT_SAVE_TRIGGER__;
    delete window.__CHEMKIT_SAVE_TRIGGER__;

    const uploadPromises = [
      AttachmentFetcher.uploadNewAttachmentsForContainer(reaction.container),
      ...reaction.products.map(prod => AttachmentFetcher.uploadNewAttachmentsForContainer(prod.container)),
    ];
    await Promise.all(uploadPromises);
    const promise = () => fetch(`/api/v1/reactions/${method === 'post' ? '' : reaction.id}`, {
      credentials: 'same-origin',
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...presence.chemkitClientIdHeader(),
        ...(trigger ? { 'x-chemkit-save-trigger': trigger } : {})
      },
      body: JSON.stringify(reaction.serialize())
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Reaction save failed: ${response.status} ${response.statusText}`);
      }
      return response.json();
    })
      .then((json) => GenericElsFetcher.uploadGenericFiles(reaction, json.reaction.id, 'Reaction')
        .then(() => ReactionsFetcher.updateAnnotationsInReaction(reaction))
        .then(() => this.fetchById(json.reaction.id)));
    return promise();
  }

  static updateAnnotationsInReaction(reaction) {
    const tasks = [];
    tasks.push(BaseFetcher.updateAnnotationsInContainer(reaction));
    reaction.products.forEach((e) => tasks.push(BaseFetcher.updateAnnotationsInContainer(e)));
    return Promise.all(tasks);
  }

  static update(reaction) {
    return ReactionsFetcher.create(reaction, 'put');
  }
}
